import { useState, useRef } from "react";
import EndPoints from "../../Api/Endpoints";

/**
 * Custom React hook to track field changes and save audit trail
 * @param {Object} initialData - The data object when page loads (before edits) - NOT USED for saveAuditWithData
 * @param {Object} auditConfig - Configuration for audit trail { clientBranchId, screenId, userName }
 * @returns {Object} { saveAuditWithData, getChanges, resetChanges }
 */
export default function useFieldChangeTracker(
  initialData = {},
  auditConfig = {}
) {
  const [changes, setChanges] = useState([]);
  const isInitialized = useRef(false);

  /**
   * Get all changed fields (if using trackChange method)
   */
  const getChanges = () => changes;

  /**
   * ✅ Save audit trail with explicit initial and current data
   * @param {Object} initialDataSnapshot - The unchanged/initial data
   * @param {Object} currentDataSnapshot - The changed/current data
   * @returns {Promise<Object>} { success, data/message }
   */
  const saveAuditWithData = async (
    initialDataSnapshot,
    currentDataSnapshot
  ) => {
    const { clientBranchId, screenId, userName } = auditConfig;

    // Validate audit config
    if (!clientBranchId || !screenId || !userName) {
      console.error("Audit config missing required fields:", auditConfig);
      return {
        success: false,
        message:
          "Missing audit configuration (clientBranchId, screenId, or userName)",
      };
    }

    // Validate input data
    if (!initialDataSnapshot || !currentDataSnapshot) {
      console.error("Initial or current data snapshot is missing");
      return {
        success: false,
        message: "Missing data snapshots for audit",
      };
    }

    // Compare initial and current data to find changes
    const auditChanges = [];
    const allKeys = new Set([
      ...Object.keys(initialDataSnapshot),
      ...Object.keys(currentDataSnapshot),
    ]);

    allKeys.forEach((key) => {
      const oldValue = initialDataSnapshot[key];
      const newValue = currentDataSnapshot[key];

      // Compare values (deep comparison for objects/arrays)
      const oldValueStr =
        typeof oldValue === "object"
          ? JSON.stringify(oldValue)
          : String(oldValue || "");
      const newValueStr =
        typeof newValue === "object"
          ? JSON.stringify(newValue)
          : String(newValue || "");

      if (oldValueStr !== newValueStr) {
        auditChanges.push({
          column_name: key,
          old_value: oldValueStr,
          new_value: newValueStr,
        });
      }
    });

    // No changes to audit
    if (auditChanges.length === 0) {
      console.log("ℹ️ No changes detected to audit");
      return { success: true, message: "No changes to audit" };
    }

    // Construct payload
    const payload = {
      client_branch_id: clientBranchId,
      screen_id: screenId,
      user_name: userName,
      changes: auditChanges,
    };

    console.log("🔍 Audit Payload:", payload);

    try {
      const accessToken =
        localStorage.getItem("other") || sessionStorage.getItem("other");

      if (!accessToken) {
        throw new Error("No access token found");
      }

      const response = await fetch(`${EndPoints.TrackerOrTransactionAudit}`, {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ Audit trail saved successfully:", result);
      return { success: true, data: result };
    } catch (error) {
      console.error("❌ Error saving audit trail:", error);
      return { success: false, message: error.message };
    }
  };

  /**
   * Reset changes (clears the changes array)
   */
  const resetChanges = () => {
    setChanges([]);
  };

  return {
    getChanges,
    resetChanges,
    saveAuditWithData,
  };
}
